import { MouseEvent, useCallback, useRef, useState } from 'react';

import { UserSubscription } from '@app-types/subscription.type';
import HDivider from '@components/h-divider/h-divider.component';

import {
    SubscriptionAction,
    SubscriptionDetailsPopover,
} from '../components/subscription-details-popover';
import SubscriptionsTable from '../components/subscriptions-table/subscriptions-table';

import styles from './user-subscriptions.module.scss';

interface UserSubscriptionsProps {
    subscriptions: UserSubscription[];
    onAction: (subscriptionId: number, action: SubscriptionAction) => void;
}

const UserSubscriptions = ({
    subscriptions,
    onAction,
}: UserSubscriptionsProps) => {
    const containerRef = useRef<HTMLTableSectionElement | null>(null);
    const [popoverProps, setPopoverProps] = useState({
        shown: false,
        top: 0,
    });
    const [selectedSubscription, setSelectedSubscription] =
        useState<UserSubscription>();

    const hasSubscriptions = (subscriptions: UserSubscription[]): boolean => {
        return !!subscriptions && subscriptions.length > 0;
    };

    const showPopover = useCallback(
        (ev: MouseEvent, id: number) => {
            ev.stopPropagation();
            const subscription = subscriptions.find((sub) => sub.id === id);
            if (!subscription) return;

            if (popoverProps.shown) {
                setPopoverProps({ shown: false, top: 0 });
                setSelectedSubscription(subscription);
                return;
            }
            const targetRect = (
                ev.target as HTMLElement
            ).getBoundingClientRect();
            const containerRect = containerRef.current!.getBoundingClientRect();

            setPopoverProps({
                shown: true,
                top: targetRect.top - containerRect.top + 30,
            });
            setSelectedSubscription(subscription);
        },
        [subscriptions]
    );

    const closePopover = () => {
        setPopoverProps({ shown: false, top: 0 });
        setSelectedSubscription(undefined);
    };

    const handleAction = (action: SubscriptionAction) => {
        switch (action) {
            case SubscriptionAction.SUSPEND:
            case SubscriptionAction.INACTIVATE:
            case SubscriptionAction.UPDATE_DATA:
            default:
                onAction(selectedSubscription!.id, action);
                break;
        }
        closePopover();
    };

    return (
        <section className={styles.subscriptions} ref={containerRef}>
            <h3>Suscripciones</h3>
            {!hasSubscriptions(subscriptions) ? (
                <div className={styles.noSubscription}>
                    No posee ninguna suscripción
                </div>
            ) : (
                <>
                    <HDivider />
                    <SubscriptionsTable
                        subscriptions={subscriptions}
                        onSelect={showPopover}
                    />
                    <SubscriptionDetailsPopover
                        shown={popoverProps.shown}
                        top={popoverProps.top}
                        subscriptionStatus={selectedSubscription?.status}
                        isDeleted={!!selectedSubscription?.deletedAt}
                        onDiscard={closePopover}
                        onAction={handleAction}
                    />
                </>
            )}
        </section>
    );
};

export default UserSubscriptions;
